import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { filter, finalize, from, map, Observable, of, race, switchMap, take, timer } from 'rxjs';
import { ESnackbar, SnackbarService } from '../../utilities/snackbar.service';
import { NO_DIALOG_CLOSE_BUTTON_PANEL_CLASS } from './dialog-close-button.service';
import { ScreenLockTokenService } from './screen-lock-token.service';
import { IScreenLockTarget, IScreenLockUnlockRequirement } from './screen-lock.interface';
import { ScreenLockService } from './screen-lock.service';

/** How long a refused write waits for the PIN before it gives up and reports the original failure. */
const UNLOCK_WAIT_MS = 180_000;

/** Makes the dialog panel transparent so the keypad's own surface is the only one on screen. */
const SCREEN_LOCK_PIN_DIALOG_PANEL_CLASS = 'screen-lock-pin-dialog-panel';

/**
 * Turns a `423 Locked` into a PIN prompt and reports whether the unlock arrived. On the guarded screen
 * itself it re-locks and shows the keypad in place; elsewhere (settings) a small dialog asks instead. See
 * SCREEN_LOCK_CONTEXT.md.
 */
@Injectable({ providedIn: 'root' })
export class ScreenLockUnlockPromptService {
  private readonly dialog = inject(MatDialog);
  private readonly screenLockService = inject(ScreenLockService);
  private readonly snackbar = inject(SnackbarService);
  private readonly tokenService = inject(ScreenLockTokenService);
  private readonly translate = inject(TranslateService);

  private isPromptOpen = false;

  /** Emits true once this screen has been unlocked, false if the operator gave up or never answered. */
  public requestUnlock(requirement: IScreenLockUnlockRequirement): Observable<boolean> {
    const [locked] = requirement.lockedEntities ?? [];

    if (!locked) {
      return of(false);
    }

    // A bulk action can hit several protected boards at once; one keypad per board is worse than telling them to handle those individually.
    if (requirement.lockedEntities.length > 1) {
      this.snackbar.open(this.translate.instant('page.screenLock.bulkUnlockRequired'), ESnackbar.error);

      return of(false);
    }

    const target: IScreenLockTarget = {
      entityId: locked.entityId,
      entityName: locked.entityName,
      entityType: requirement.entityType,
    };

    if (this.relockRunningScreen(target)) {
      return this.waitForUnlock(target);
    }

    return this.openPrompt(target);
  }

  /** True when the lock for this screen is running here, so the overlay can do the asking. */
  private relockRunningScreen(target: IScreenLockTarget): boolean {
    const runningTarget: IScreenLockTarget | null = this.screenLockService.target();
    const isSameScreen: boolean =
      !!runningTarget && runningTarget.entityType === target.entityType && runningTarget.entityId === target.entityId;

    if (!this.screenLockService.isRunning() || !isSameScreen) {
      return false;
    }

    this.screenLockService.lock();
    this.screenLockService.openKeypad();

    return true;
  }

  /** The on-screen keypad has no meaningful cancel, so the wait is bounded by time, not by dismissal. */
  private waitForUnlock(target: IScreenLockTarget): Observable<boolean> {
    return race(
      this.tokenService.unlocked$.pipe(
        filter(
          (unlocked: IScreenLockTarget) =>
            unlocked.entityType === target.entityType && unlocked.entityId === target.entityId,
        ),
        take(1),
        map(() => true),
      ),
      timer(UNLOCK_WAIT_MS).pipe(map(() => false)),
    );
  }

  private openPrompt(target: IScreenLockTarget): Observable<boolean> {
    if (this.isPromptOpen) {
      return of(false);
    }

    this.isPromptOpen = true;

    let dialogRef: MatDialogRef<unknown, boolean> | null = null;

    // Lazy import: this service is reached from the HTTP interceptor, so pulling the component in eagerly risks cyclic dependency errors.
    return from(import('../../shared/components/screen-lock-pin-dialog/screen-lock-pin-dialog.component')).pipe(
      switchMap(({ ScreenLockPinDialogComponent }) => {
        dialogRef = this.dialog.open(ScreenLockPinDialogComponent, {
          autoFocus: false,
          data: target,
          // The keypad brings its own close button; the generic one would be the second X on screen.
          panelClass: [SCREEN_LOCK_PIN_DIALOG_PANEL_CLASS, NO_DIALOG_CLOSE_BUTTON_PANEL_CLASS],
          restoreFocus: false,
        });

        return dialogRef.afterClosed();
      }),
      take(1),
      map((isUnlocked: boolean | undefined) => {
        // Closed on its own, so `finalize` has nothing left to tidy.
        dialogRef = null;

        return !!isUnlocked;
      }),
      // `finalize`, not the `map`: the caller is an HTTP chain that can be unsubscribed (a cancelled request,
      // a `switchMap` dropping the previous one) and the dynamic import can reject. Clearing the flag only on
      // a normal close left it stuck at `true`, after which every later 423 resolved `false` without ever
      // asking for the PIN — and the dialog stayed on screen with nobody listening. See SCREEN_LOCK_CONTEXT.md.
      finalize(() => {
        this.isPromptOpen = false;
        dialogRef?.close(false);
        dialogRef = null;
      }),
    );
  }
}
